import React, { useCallback, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";

import store from "./redux/store";
import Login from "./components/Login";
import Home from "./components/Home";
import EdgeList from "./pages/EdgeList";
import EdgeDetail from "./pages/EdgeDetail";
import Marketplace from "./pages/Marketplace";
import TenantList from "./pages/TenantList";
import AlarmList from "./pages/AlarmList";
import VpnUsers from "./pages/VpnUsers";
import MainLayout from "./components/Layout/MainLayout";
import { base_name } from "./config";
import Forgot from "./pages/Forgot";
import ResetPassword from "./pages/Reset";
import FirstLoginPassword from "./pages/FirstLoginPassword";
import SessionExpiryDialog from "./components/SessionExpiryDialog";
import { useSessionTimers } from "./hooks/useSessionTimers";
import { refreshAccess, logoutApi,getMe } from "./api/authApi";
import {
  setAccess,
  setMe,
  consumeExtension,
  logout as logoutAction,
} from "./redux/sessionSlice";
import Profile from "./components/profile";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * AppShell:
 * - Uygulama açılışında cookie varsa refresh yapıp access token üretir
 * - access token gelince /me çekip redux'a yazar
 * - Session popup + expire timer burada yönetilir
 */

function AppShell() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const accessToken = useSelector((s) => s.session.accessToken);
  const accessExp = useSelector((s) => s.session.accessExp);
  const me = useSelector((s) => s.session.me);
  const status = useSelector((s) => s.session.status);
  const extensionsLeft = useSelector((s) => s.session.extensionsLeft);

  const [booting, setBooting] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canExtend = status === "authenticated" && extensionsLeft > 0;
  const WARNING_BEFORE_MS = 60 * 1000;

  const doLogout = useCallback(async () => {
    try {
      await logoutApi(); // refresh cookie silinsin
    } catch (e) {
      // backend logout patlasa bile frontend logout yapacağız
    } finally {
      localStorage.removeItem("user_info");
      dispatch(logoutAction());
      setDialogOpen(false);
      navigate("/", { replace: true });
    }
  }, [dispatch, navigate]);

  /**
   * Sayfa ilk açıldığında 1 kere çalışır.
   * Cookie varsa /auth/refresh çağırıp access token alır.
   * Not: dependency boş bırakıyoruz -> 1 kere çalışsın diye.
   */
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        // accessToken zaten varsa refresh yapmaya gerek yok
        if (accessToken) return;

        const data = await refreshAccess(); // cookie ile
        if (cancelled) return;

        dispatch(setAccess({ token: data.access_token, exp: data.exp }));
      } catch (e) {
        // cookie yoksa / refresh başarısızsa -> login ekranında kalırız
        dispatch(logoutAction());
      } finally {
        if (!cancelled) setBooting(false);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap: sadece mount'ta, cookie ile refresh
  }, []);

  /**
   * accessToken geldiğinde /auth/me çağırır ve redux'a setMe yapar.
   */
  useEffect(() => {
    let cancelled = false;

    async function loadMe() {
      if (!accessToken) return;
      if (me) return; // zaten set edilmişse tekrar çekme

      try {
        const meData = await getMe(accessToken);
        if (cancelled) return;
        dispatch(setMe(meData));
      } catch (e) {
        dispatch(logoutAction());
        navigate("/", { replace: true });
      }
    }

    loadMe();

    return () => {
      cancelled = true;
    };
  }, [accessToken, me, dispatch, navigate]);

  // Token bitmeden uyarı aç
  const onWarning = useCallback(() => {
    if (status !== "authenticated") return;
    setDialogOpen(true);
  }, [status]);

  // Token süresi dolunca kesin logout
  const onExpire = useCallback(() => {
    doLogout();
  }, [doLogout]);

  // boot bitmeden timer kurma 
  useSessionTimers(booting ? null : accessExp, WARNING_BEFORE_MS, onWarning, onExpire);

  // Uzat butonu
  const handleExtend = useCallback(async () => {
    if (!(status === "authenticated" && extensionsLeft > 0)) {
      doLogout();
      return;
    }

    try {
      const data = await refreshAccess(); // cookie ile
      dispatch(setAccess({ token: data.access_token, exp: data.exp }));
      dispatch(consumeExtension()); // 1 hakkı düş
      setDialogOpen(false);

    } catch (e) {
      doLogout();
    }
  }, [status, extensionsLeft, dispatch, doLogout]);

  if (booting) {
    return <div style={{ padding: 24 }}>Yükleniyor...</div>;
  }

  const isAuthenticated = !!accessToken;
  const mustChangePassword = Boolean(me?.must_change_password);

  if (isAuthenticated && mustChangePassword) {
    return (
      <>
        <SessionExpiryDialog
          open={dialogOpen}
          canExtend={canExtend}
          onExtend={handleExtend}
          onLogout={doLogout}
        />
        <Routes>
          <Route path="/change-password-required" element={<FirstLoginPassword />} />
          <Route path="*" element={<Navigate to="/change-password-required" replace />} />
        </Routes>
      </>
    );
  }

  // Login sayfasi icin layout gosterme
  if (!isAuthenticated) {
    return (
      <>
        <SessionExpiryDialog
          open={dialogOpen}
          canExtend={canExtend}
          onExtend={handleExtend}
          onLogout={doLogout}
        />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<Forgot />} />  
        <Route path="/reset-password/:reset_code" element={<ResetPassword />} />  
        <Route path="*" element={<Login />} />
      </Routes>
      </>
    );
  }

  return (
    <>
      <SessionExpiryDialog
        open={dialogOpen}
        canExtend={canExtend}
        onExtend={handleExtend}
        onLogout={doLogout}
      />

      <MainLayout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/edges" element={<EdgeList />} />
          <Route path="/edges/:edgeDbId" element={<EdgeDetail />} />
          <Route path="/tenants" element={<TenantList />} />
          <Route path="/alarms" element={<AlarmList />} />
          <Route path="/vpn-users" element={<VpnUsers />} />
          <Route path="/marketplace" element={<Marketplace />} />

      </Routes>
      </MainLayout>
    </>
  );
}

/**
 * App:
 * - Provider + Router sarmalı
 * - AppShell Router içinde olmalı (useNavigate kullandığı için)
 */
export default function App() {
  return (
    <Provider store={store}>
      <Router basename={base_name ? `/${base_name}` : ""}>
        <ToastContainer position="top-center" autoClose={2500} hideProgressBar={false} newestOnTop />
        <AppShell />
      </Router>
    </Provider>
  );
}
